import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useLoggedMutation } from "@/hooks/use-action-log-session";
import { toCellList, toCellRecord } from "@/lib/schedule-cells";

/** The job's schedule overrides, with cells keyed by column. */
export function useScheduleOverrides(jobId: Id<"takeoffPilotJobs"> | undefined) {
  const { data, isPending } = useQuery(
    convexQuery(api.takeoffPilot.scheduleOverrides.listByJob, jobId ? { jobId } : "skip")
  );
  const overrides = useMemo(
    () => (data ?? []).map((override) => ({ ...override, cells: toCellRecord(override.cells) })),
    [data]
  );
  return { overrides, isPending };
}

/** Saves override cells keyed by column. */
export function useUpsertScheduleOverride() {
  const upsert = useLoggedMutation(api.takeoffPilot.scheduleOverrides.upsert);
  return useCallback(
    (args: {
      jobId: Id<"takeoffPilotJobs">;
      tagId: Id<"takeoffPilotExtractedTags">;
      cells: Record<string, string>;
    }) => upsert({ ...args, cells: toCellList(args.cells) }),
    [upsert]
  );
}

export function useAddScheduleOverrideColumn() {
  return useLoggedMutation(api.takeoffPilot.scheduleOverrides.addColumn);
}

export function useRemoveScheduleOverrideCells() {
  return useLoggedMutation(api.takeoffPilot.scheduleOverrides.removeCells);
}

export function useResetScheduleOverride() {
  return useLoggedMutation(api.takeoffPilot.scheduleOverrides.resetByTag);
}
